import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BILLING_CHANGED } from "../lib/billingEvents";

/**
 * useUsage — single source of truth for the signed-in user's quota usage,
 * per-plan limits, and credit balance, fetched from
 * GET /api/payments/subscription/status. Replaces hardcoded client-side caps.
 *
 * Returns:
 *   usage          { links, clicks, ai_credits, dynamic_qr }
 *   limits         per-dimension caps (null = unlimited) keyed by *_per_month /
 *                  custom_domains / team_seats / api_keys / webhooks
 *   creditBalance  { ai_rollover, ai_purchased, link_pack }
 *   plan, effectivePlan, loading, error, refetch
 */
export function useUsage() {
  const { user } = useAuth();
  const [state, setState] = useState({
    usage: { links: 0, clicks: 0, ai_credits: 0, dynamic_qr: 0 },
    limits: {},
    creditBalance: { ai_rollover: 0, ai_purchased: 0, link_pack: 0 },
    plan: "free",
    effectivePlan: "free",
    loading: true,
    error: null,
  });

  const refetch = useCallback(async () => {
    if (!user) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    try {
      const { data } = await api.get("/payments/subscription/status");
      setState({
        usage: data.usage || { links: 0, clicks: 0, ai_credits: 0, dynamic_qr: 0 },
        limits: data.limits || {},
        creditBalance: data.credit_balance || { ai_rollover: 0, ai_purchased: 0, link_pack: 0 },
        plan: data.plan || "free",
        effectivePlan: data.effective_plan || data.plan || "free",
        loading: false,
        error: null,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e }));
    }
  }, [user]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Live updates without a reload: refetch on tab focus/visibility and on any
  // billing change broadcast. Every useUsage instance reacts together, so the
  // credit meter, billing page, and usage bars stay in sync after an upgrade.
  useEffect(() => {
    if (!user) return undefined;
    let last = 0;
    const onFocus = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - last < 5000) return;
      last = now;
      refetch();
    };
    const onBilling = () => {
      last = Date.now();
      refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener(BILLING_CHANGED, onBilling);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener(BILLING_CHANGED, onBilling);
    };
  }, [user, refetch]);

  return { ...state, refetch };
}

export default useUsage;
